import React, { useMemo, useState, useEffect } from "react";
import { displayFormatDate, toggleMenu } from "../../utils/helpers";
import PageTitle from "../../components/PageTitle";
import axios from "axios";
import Cookies from "js-cookie";
import { TailSpin } from "react-loader-spinner";
import NoRecordsAvailable from "../../components/NoRecordsAvailable";
import { API_GET_RAISED_CONCERNS_BY_EMPLOYEE } from "../../config/Endpoints";
import Badge from "../../components/Badge";
import RaiseConcern from "../Dashboard/RaiseConcern";
import { ResponsiveDataTable } from "../../v2/components/ui/responsive-table/index.ts";
import { Button } from "../../v2/components/ui/button/button.tsx";

const MyConcerns = ({ title, description }) => {
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRequests, setTotalRequests] = useState(0);
  const [raiseConcernOpen, setRaiseConcernOpen] = useState(false);
  const accessToken = Cookies.get("accessToken");

  const columns = useMemo(
    () => [
      {
        id: "concernRequest",
        header: "Raised Concern",
        accessor: "concernRequest",
        sortable: true,
        truncate: true,
      },
      {
        id: "concernDate",
        header: "Concern Date",
        accessor: "concernDate",
        sortable: true,
        getSortValue: (row) =>
          new Date(row._original.concernDate).getTime(),
      },
      {
        id: "punchIn",
        header: "Punch In",
        accessor: "punchIn",
        sortable: true,
      },
      {
        id: "punchOut",
        header: "Punch Out",
        accessor: "punchOut",
        sortable: true,
      },
      {
        id: "totalHours",
        header: "Total Hours",
        accessor: "totalHours",
        sortable: true,
      },
      {
        id: "status",
        header: "Status",
        accessor: "status",
        sortable: true,
        nowrap: true,
        render: (row) => (
          <Badge
            type={
              row.status === "PENDING"
                ? "warning"
                : row.status === "REJECTED"
                  ? "danger"
                  : "success"
            }
            className="text-capitalize"
          >
            {row.status === "PENDING"
              ? "Pending"
              : row.status === "REJECTED"
                ? "Rejected"
                : "Approved"}
          </Badge>
        ),
      },
    ],
    [],
  );

  useEffect(() => {
    fetchConcerns();
  }, [currentPage, limit]);

  const fetchConcerns = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: currentPage - 1,
        size: limit,
      };

      const res = await axios.get(API_GET_RAISED_CONCERNS_BY_EMPLOYEE, {
        params,
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const formattedData = res.data.content.map((item) => ({
        empName: item.empName,
        concernRequest: item.reason,
        concernDate: displayFormatDate(item.concernDate),
        punchIn: item.punchInTime ?? "NA",
        punchOut: item.punchOutTime ?? "NA",
        totalHours: item.totalHour ?? "0",
        status: item.status ?? "-",
        _original: item,
      }));

      setTableData(formattedData);
      setTotalRequests(res.data?.totalElements);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage);
  const handleLimitChange = (nextSize) => {
    setLimit(nextSize);
    setCurrentPage(1);
  };

  const tableRows = useMemo(
    () => tableData.map((row) => ({ ...row })),
    [tableData],
  );

  return (
    <div className="wrapper-body">
      {isLoading && (
        <div className="overlay">
          <div className="mt-5">
            <TailSpin height="100" width="100" color="blue" visible={true} />
          </div>
        </div>
      )}

      <div className="page-heading mb-4 d-flex gap-2 justify-content-end align-items-center flex-wrap">
       
        <div>
          <Button
            type="button"
            variant="accent"
            onClick={() => setRaiseConcernOpen(true)}
          >
            Raise Concern
          </Button>
        </div>
      </div>

      {!isLoading && tableData.length > 0 ? (
        <ResponsiveDataTable
          columns={columns}
          data={tableRows}
          serialNumber
          aria-label="My raised concerns"
          page={currentPage}
          pageSize={limit}
          totalItems={totalRequests ?? 0}
          onPageChange={handlePageChange}
          onPageSizeChange={handleLimitChange}
          pageSizeOptions={[5, 10, 20, 30, 40]}
          totalLabel={
            <>
              Total Requests: <strong>{totalRequests ?? 0}</strong>
            </>
          }
        />
      ) : (
        <div className="text-center">
          {!isLoading && <NoRecordsAvailable />}
        </div>
      )}

      <RaiseConcern
        open={raiseConcernOpen}
        onClose={() => setRaiseConcernOpen(false)}
        onSuccess={fetchConcerns}
      />
    </div>
  );
};

export default MyConcerns;
